
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  ArrowRight,
  Code2,
  BrainCircuit,
  Workflow,
  ShieldCheck,
  Database,
  GraduationCap,
  CheckCircle2,
} from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// =====================================
// ANIMATIONS
// =====================================

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.12 },
  transition: { duration: 0.5 },
};

// =====================================
// SERVICES
// =====================================

const services = [
  {
    number: "01",
    icon: Code2,
    title: "Custom Software & Applications",
    description:
      "We build custom applications, digital platforms, CRM and ERP systems tailored to your business needs.",
    features: [
      "Web and mobile applications",
      "CRM, ERP and business software",
      "Custom digital platforms",
    ],
  },
  {
    number: "02",
    icon: BrainCircuit,
    title: "Artificial Intelligence Solutions",
    description:
      "We develop practical AI solutions that simplify complex tasks, process information, and support smarter decisions.",
    features: [
      "AI-powered tools",
      "Intelligent data processing",
      "AI integrations",
    ],
  },
  {
    number: "03",
    icon: Workflow,
    title: "Business Process Automation",
    description:
      "We automate repetitive tasks and connect business systems to improve efficiency and reduce manual work.",
    features: [
      "Workflow automation",
      "System integrations",
      "Process optimization",
    ],
  },
  {
    number: "04",
    icon: ShieldCheck,
    title: "Cybersecurity Solutions",
    description:
      "We help businesses protect their digital systems, strengthen security, and manage cyber risks.",
    features: [
      "Cybersecurity consulting",
      "Security solutions",
      "Risk assessment",
    ],
  },
  {
    number: "05",
    icon: Database,
    title: "Intelligence & Information Systems",
    description:
      "We build intelligent platforms that organize, analyze, and visualize information to support better decisions.",
    features: [
      "Information management",
      "Data visualization",
      "Intelligence platforms",
    ],
  },
  {
    number: "06",
    icon: GraduationCap,
    title: "Cybersecurity Education",
    description:
      "We provide practical cybersecurity training to help individuals and organizations develop essential digital security skills.",
    features: [
      "Cybersecurity training",
      "Practical technical skills",
      "Professional development",
    ],
  },
];

// =====================================
// ABOUT US PAGE
// =====================================

export default function AboutUs() {
  // =====================================
  // SEO METADATA
  // =====================================

  useEffect(() => {
    document.title =
      "About FrontSpace | Software, AI & Cybersecurity";

    const description =
      "Discover FrontSpace's custom software development, AI solutions, business automation, cybersecurity services, and digital technology expertise.";

    let meta = document.querySelector(
      'meta[name="description"]'
    );

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", description);
  }, []);

  return (
    <div
      className="min-h-screen text-white overflow-x-hidden"
      style={{ backgroundColor: "#050814" }}
    >
      {/* ===================================== */}
      {/* NAVBAR */}
      {/* ===================================== */}

      <Navbar />

      {/* ===================================== */}
      {/* SHORT INTRODUCTION */}
      {/* ===================================== */}

      <section className="relative pt-28 pb-8 lg:pt-32 lg:pb-10 overflow-hidden">
        <div className="absolute inset-0 cyber-grid-bg opacity-30 pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <h1
              className="text-slate-200 text-base sm:text-lg lg:text-xl font-normal leading-relaxed max-w-5xl mx-auto text-center"
              style={{
                textWrap: "balance",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              FrontSpace develops custom software,
              AI-powered solutions, business automation,
              and cybersecurity technologies that help
              organizations solve challenges, improve
              efficiency, and innovate.
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ===================================== */}
      {/* WHAT WE DO */}
      {/* ===================================== */}

      <section
        id="what-we-do"
        className="relative pt-6 pb-14 lg:pt-8 lg:pb-16"
        style={{
          background:
            "linear-gradient(180deg, #050814 0%, #08152B 50%, #050814 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          {/* Section heading */}

          <motion.div
            {...fadeUp}
            className="text-center mb-10"
          >
            <h2
              className="text-2xl sm:text-3xl font-bold mb-3"
              style={{
                fontFamily: "Outfit, sans-serif",
              }}
            >
              What We{" "}
              <span className="text-[#00F0FF]">
                Do
              </span>
            </h2>

            <p className="text-slate-400 text-base">
              Explore our technology solutions.
            </p>
          </motion.div>

          {/* ===================================== */}
          {/* SERVICE CARDS */}
          {/* ===================================== */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;

              return (
                <motion.article
                  key={service.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{
                    once: true,
                    amount: 0.1,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: (index % 2) * 0.08,
                  }}
                  className="cyber-card p-6 sm:p-8 h-full"
                >
                  {/* Icon and number */}

                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/20">
                      <Icon
                        size={24}
                        className="text-[#00F0FF]"
                      />
                    </div>

                    <span className="text-slate-600 text-sm font-bold tracking-widest">
                      {service.number}
                    </span>
                  </div>

                  {/* Service title */}

                  <h3
                    className="text-xl sm:text-2xl font-bold mb-4"
                    style={{
                      fontFamily: "Outfit, sans-serif",
                    }}
                  >
                    {service.title}
                  </h3>

                  {/* Description */}

                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Features */}

                  <div className="space-y-3">
                    {service.features.map((feature) => (
                      <div
                        key={feature}
                        className="flex items-start gap-3"
                      >
                        <CheckCircle2
                          size={17}
                          className="text-[#00F0FF] flex-shrink-0 mt-0.5"
                        />

                        <span className="text-slate-400 text-sm leading-relaxed">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* OUR APPROACH */}
      {/* ===================================== */}

      <section className="relative py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            {...fadeUp}
            className="text-center"
          >
            <div className="cyber-badge mx-auto mb-5">
              OUR APPROACH
            </div>

            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-6"
              style={{
                fontFamily: "Outfit, sans-serif",
              }}
            >
              Understand the Challenge.
              <br />

              <span className="text-[#00F0FF]">
                Build the Right Solution.
              </span>
            </h2>

            <p className="text-slate-400 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
              Every business has different needs.
              We focus on understanding your goals
              and developing practical digital solutions
              that fit the way you work.
            </p>

            <div className="mt-8">
              <Link
                to="/services"
                className="cyber-btn-primary inline-flex items-center gap-3"
              >
                Explore Our Services
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================================== */}
      {/* FOOTER */}
      {/* ===================================== */}

      <Footer />
    </div>
  );
}
